import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { track } from "../lib/analytics.ts";

/**
 * A label with an (i) button. The explanation shows on hover, on keyboard
 * focus, and on tap (phones have no hover). Escape or a tap elsewhere closes it.
 */
export function InfoTip({
  label,
  children,
  trackAs,
}: {
  label: ReactNode;
  children: ReactNode;
  /** Short fixed name sent to analytics when the tip is opened. */
  trackAs?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  const counted = useRef(false);

  // Count the first opening per tip per visit, not every hover.
  useEffect(() => {
    if (open && trackAs && !counted.current) {
      counted.current = true;
      track("info-opened", { tile: trackAs });
    }
  }, [open, trackAs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className={open ? "infotip open" : "infotip"}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span>{label}</span>
      <button
        type="button"
        className="infotip-btn"
        aria-label="What does this mean?"
        aria-expanded={open}
        aria-describedby={id}
        onClick={() => setOpen((o) => !o)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        i
      </button>
      <span role="tooltip" id={id} className="infotip-body">
        {children}
      </span>
    </span>
  );
}
